import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Send, MapPin, Image as ImageIcon, Trash2, ArrowLeft, Clock } from 'lucide-react';
import { SEO } from '../components/shared/SEO';
import { Moment } from '../types';

const Moments: React.FC = () => {
    const [moments, setMoments] = useState<Moment[]>([]);
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [content, setContent] = useState('');
    const [location, setLocation] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const apiUrl = '/api/blog-admin/moments';

    const fetchMoments = async () => {
        try {
            const response = await fetch(apiUrl);
            if (response.ok) {
                const data = await response.json();
                setMoments(data);
            }
        } catch (error) {
            console.error('Error fetching moments:', error);
        } finally {
            setLoading(false);
        }
    };

    const checkAuth = async () => {
        try {
            const res = await fetch('/api/auth/me', { credentials: 'include' });
            if (res.ok) {
                setIsAdmin(true);
            }
        } catch (e) {
            console.log('Not logged in as admin');
        }
    };

    useEffect(() => {
        fetchMoments();
        checkAuth();
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!content.trim()) return;

        setIsSubmitting(true);
        try {
            const res = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ content, location, images: [] }),
            });

            if (res.ok) {
                setContent('');
                setLocation('');
                fetchMoments();
            } else {
                alert('发布失败，请检查登录状态');
            }
        } catch (error) {
            alert('发布失败: ' + error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('确定要删除这条片刻吗？')) return;
        
        try {
            const res = await fetch(`${apiUrl}/${id}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            
            if (res.ok) {
                fetchMoments();
            } else {
                alert('删除失败');
            }
        } catch (e) {
            alert('删除出错');
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleString('zh-CN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50/50 dark:from-[#0b1220] dark:via-slate-900 dark:to-slate-800 py-8 sm:py-12 px-3 sm:px-4">
            <SEO 
                title="片刻"
                description="记录生活、工作中的零碎想法和瞬间"
            />
            <div className="w-full max-w-3xl mx-auto">
                
                {/* Header */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                >
                    <Link 
                        to="/" 
                        className="inline-flex items-center gap-2 text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400 mb-4 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        返回首页
                    </Link>
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-sky-500 to-emerald-500 dark:from-blue-400 dark:via-sky-300 dark:to-emerald-300 mb-2">
                        片刻
                    </h1>
                    <p className="text-slate-500 dark:text-slate-400 text-sm">
                        记录生活、工作中的零碎想法
                    </p>
                </motion.section>

                {/* Create Form - Admin Only */}
                {isAdmin && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl sm:rounded-[30px] border border-white/80 dark:border-slate-800/80 shadow-[0_24px_70px_-44px_rgba(15,23,42,0.25)] p-4 sm:p-6 mb-8"
                    >
                        <form onSubmit={handleSubmit}>
                            <textarea
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                                placeholder="此刻在想什么..."
                                className="w-full bg-slate-50/80 dark:bg-slate-800/80 border border-slate-200/60 dark:border-slate-700/60 rounded-xl sm:rounded-2xl p-3 sm:p-4 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none min-h-[100px] sm:min-h-[120px]"
                            />
                            <div className="flex flex-wrap items-center justify-between mt-3 sm:mt-4 gap-3">
                                <div className="flex flex-wrap items-center gap-2">
                                    <div className="flex items-center bg-slate-50/80 dark:bg-slate-800/80 border border-slate-200/60 dark:border-slate-700/60 rounded-lg px-3 py-1.5 focus-within:ring-2 focus-within:ring-blue-500 max-w-[200px]">
                                        <MapPin className="w-4 h-4 text-slate-400 mr-2 flex-shrink-0" />
                                        <input
                                            type="text"
                                            value={location}
                                            onChange={(e) => setLocation(e.target.value)}
                                            placeholder="添加位置..."
                                            className="bg-transparent text-sm text-slate-700 dark:text-slate-300 outline-none w-full"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition"
                                        title="上传图片(即将支持)"
                                    >
                                        <ImageIcon className="w-5 h-5" />
                                    </button>
                                </div>
                                <button
                                    type="submit"
                                    disabled={!content.trim() || isSubmitting}
                                    className="flex items-center bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-medium transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-600/20"
                                >
                                    <Send className="w-4 h-4 mr-2" />
                                    {isSubmitting ? '发送中' : '发布'}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                )}

                {/* Moments List */}
                <div className="space-y-4 sm:space-y-6">
                    {loading ? (
                        <div className="text-center py-10 text-slate-500 dark:text-slate-400">
                            加载中...
                        </div>
                    ) : moments.length === 0 ? (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl sm:rounded-[30px] border border-white/80 dark:border-slate-800/80 p-10 text-center"
                        >
                            <Clock className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
                            <p className="text-slate-500 dark:text-slate-400">暂无片刻</p>
                        </motion.div>
                    ) : (
                        moments.map((moment, index) => (
                            <motion.div
                                key={moment.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl sm:rounded-[30px] border border-white/80 dark:border-slate-800/80 shadow-[0_18px_48px_-36px_rgba(15,23,42,0.22)] p-5 sm:p-6 relative group"
                            >
                                {isAdmin && (
                                    <button 
                                        onClick={() => handleDelete(moment.id)}
                                        className="absolute top-4 right-4 p-2 text-slate-400 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 transition opacity-0 group-hover:opacity-100"
                                        title="删除片刻"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                )}
                                
                                <div className="flex flex-wrap justify-between items-center mb-4 gap-2 pr-10">
                                    <div className="text-sm text-slate-500 dark:text-slate-400 flex items-center">
                                        <Clock className="w-4 h-4 mr-1.5" />
                                        {formatDate(moment.createdAt)}
                                    </div>
                                    {moment.location && (
                                        <div className="text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2.5 py-1 rounded-full flex items-center">
                                            <MapPin className="w-3 h-3 mr-1" />
                                            {moment.location}
                                        </div>
                                    )}
                                </div>
                                
                                <div className="prose dark:prose-invert max-w-none text-slate-700 dark:text-slate-200 min-w-0 break-words whitespace-pre-wrap leading-relaxed text-base sm:text-lg">
                                    {moment.content}
                                </div>
                                
                                {moment.images && moment.images.length > 0 && (
                                    <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                                        {moment.images.map((img, idx) => (
                                            <img 
                                                key={idx} 
                                                src={img} 
                                                alt={`配图 ${idx + 1}`} 
                                                className="rounded-xl object-cover aspect-square w-full"
                                            />
                                        ))}
                                    </div>
                                )}
                            </motion.div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default Moments;
